<template>
  <base-wrapper :loading="loading">
    <div class="space-y-6">
      <payment-order-detail-card :order="order" />

      <template v-if="pageLoaded">
        <create-payment-form
          v-if="needCreatePayment"
          :min-amount="order.data.attributes.grand_total_price"
          :paid="paidAmount"
          @save="onCreatePayment"
        />

        <payment-list
          :payments="payments"
          :order-price="order.data.attributes.grand_total_price"
          :order-code="`${order.data.attributes.origin_code}/${order.data.attributes.destination_code}`"
          editable
          order-valid
          with-total-payment-amount
          with-total-payment-remainder
          :payment-amount="paidAmount"
          :order-id="$route.params.id"
          :acc-payment-reason="order.data.attributes.acc_payment_reason"
          @reload="loadPayments"
          @verified="onVerifiedPayment"
        />

        <div :class="['flex', order.data.attributes.is_valid_for_packing ? 'justify-end' : 'justify-between']">
          <base-button v-if="!order.data.attributes.is_valid_for_packing" color="white" @click="visibleDeleteConfirm = true"
            >Batalkan Faktur Penjualan</base-button
          >
          <div class="flex gap-2">
            <base-button v-if="accAtasanFormShown" @click="visibleAccAtasanConfirmation = true">
              <Icon icon="heroicons:document-solid" />
              Acc Atasan
            </base-button>
            <base-button :to="{ name: 'kasir.pembayaran' }" color="white"
              >Kembali</base-button
            >
          </div>
        </div>
      </template>
    </div>

    <acc-atasan-confirmation :visible="visibleAccAtasanConfirmation" :order-id="order.data.id" @confirmed="$router.push({ name: 'kasir.pembayaran' });" @close="visibleAccAtasanConfirmation = false" />

    <base-confirm
      message="Sudah ada faktur pembayaran. Anda yakin ingin membatalkan?"
      :visible="visibleDeleteConfirm"
      @close="visibleDeleteConfirm = false"
      @confirmed="onConfirmDelete"
    />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import PaymentOrderDetailCard from '@/components/payment/payment-order-detail-card.vue';
import CreatePaymentForm from '@/components/payment/create-payment-form.vue';
import PaymentList from '@/components/payment/payment-list.vue';
import BaseConfirm from '@/components/base/BaseConfirm.vue';
import AccAtasanConfirmation from '@/components/kasir/acc-atasan-confirmation.vue';

export default {
  components: {
    PaymentOrderDetailCard,
    CreatePaymentForm,
    PaymentList,
    BaseConfirm,
    AccAtasanConfirmation
  },
  mixins: [requestMixin],
  data() {
    return {
      order: {
        data: {
          attributes: {},
          relationships: {
            'destination-office': {
              data: {
                id: null,
              },
            },
          },
        },
        included: [],
      },
      payments: {
        data: [],
      },
      loadingOrder: true,
      loadingCreatePayment: false,
      loadingPayments: false,
      loadingCancel: false,
      pageLoaded: false,
      visibleDeleteConfirm: false,
      visibleAccAtasanConfirmation: false
    };
  },
  computed: {
    loading() {
      return (
        this.loadingOrder ||
        this.loadingCreatePayment ||
        this.loadingPayments ||
        this.loadingCancel
      );
    },
    orderDestinationOffice() {
      return this.getSingleIncluded(
        this.order,
        this.order.data.relationships['destination-office'].data.id
      );
    },
    needCreatePayment() {
      if (!this.payments.data.length) {
        return true;
      }

      if (
        this.payments.data.some((payment) => !payment.attributes.is_verified)
      ) {
        return false;
      }

      if (!this.order.data.attributes.is_paid) {
        return true;
      }

      return false;
    },
    paidAmount() {
      return this.payments.data.reduce(
        (total, payment) => total + payment.attributes.cashier_total_amount,
        0
      );
    },
    accAtasanFormShown() {
      return (
        this.needCreatePayment && !this.order.data.attributes.acc_payment_reason
      );
    },
  },
  methods: {
    async createPaymentMethods(payment, paymentMethods) {
      const requests = paymentMethods.map((paymentMethod) => {
        return this.request(`/api/v1/payment-methods`, {
          method: 'post',
          data: {
            data: {
              type: 'payment-methods',
              attributes: {
                payment_amount: Number(
                  paymentMethod.amount.replace(/\D/gi, '')
                ),
                cashier_payment_amount: Number(
                  paymentMethod.amount.replace(/\D/gi, '')
                ),
                receipt_number: paymentMethod.receipt_number,
              },
              relationships: {
                payment: {
                  data: {
                    type: 'payments',
                    id: payment.data.id,
                  },
                },
                'payment-method-type': {
                  data: {
                    type: 'payment-method-types',
                    id: paymentMethod.paymentMethodTypeId,
                  },
                },
                ...(paymentMethod.officeBankId
                  ? {
                      'office-bank': {
                        data: {
                          type: 'office-banks',
                          id: paymentMethod.officeBankId,
                        },
                      },
                    }
                  : {}),
              },
            },
          },
        });
      });

      await Promise.all(requests);
    },
    async loadOrder() {
      this.loadingOrder = true;

      const orderId = this.$route.params.id;

      const [res, error] = await this.request(`/api/v1/orders/${orderId}`, {
        params: {
          'fields[orders]':
            'paid_by,origin_code,notes,destination_code,origin_warehouse_code,area_code,grand_total_price,order_shipment,is_paid,is_valid_for_packing,destination-office,origin-office,acc_payment_reason',
          'fields[offices]': 'office_type,code,name,temp_code,is_active',
          include: 'destination-office,origin-office',
        },
      });

      if (!error) {
        this.order = res;
      }

      this.loadingOrder = false;
    },
    async loadPayments() {
      this.loadingPayments = true;

      const orderId = this.$route.params.id;

      const [res, error] = await this.request(
        `/api/v1/orders/${orderId}/payments`,
        {
          params: {
            'fields[payments]':
              'is_verified,cashier_total_amount,code,destination_office_code,origin_warehouse_code,state,createdAt,verified_by',
          },
        }
      );

      if (!error) {
        this.payments = res;
      }

      this.loadingPayments = false;
    },
    async onConfirmDelete() {
      this.loadingCancel = true;

      const [, error] = await this.request(
        `/api/v1/orders/${this.order.data.id}/-actions/cancelPayment`,
        {
          method: 'patch',
          data: {
            cancel_payment_description: 'Cancel',
          },
        }
      );

      if (!error) {
        this.$router.push({ name: 'kasir.pembayaran' });
      }

      this.loadingCancel = false;
    },
    async onCreatePayment(paymentMethods) {
      this.loadingCreatePayment = true;

      const [payment, error] = await this.request(`/api/v1/payments`, {
        method: 'post',
        data: {
          data: {
            type: 'payments',
            relationships: {
              order: {
                data: {
                  type: 'orders',
                  id: this.order.data.id,
                },
              },
            },
          },
        },
      });

      if (!error) {
        await this.createPaymentMethods(payment, paymentMethods);

        this.loadPayments();
      }

      this.loadingCreatePayment = false;
    },
    onVerifiedPayment() {
      this.loadOrder();
      this.loadPayments();
    },
  },
  async created() {
    await Promise.all([this.loadOrder(), this.loadPayments()]);

    this.pageLoaded = true;
  },
};
</script>
